import { useState, useEffect, useCallback } from 'react'
import { Button, Select, Space, Statistic, Table, Tag, Tooltip, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import ReactECharts from 'echarts-for-react'
import * as uiApi from '@/api/uiAutomation'
import { useProjectStore } from '@/stores/project'
import { useThemeStore } from '@/stores/theme'
import type { UIFlakyItem, UITrendData } from '@/types'
import '../../styles/aurora-page.css'

const { Title } = Typography

const DAYS_OPTIONS = [
  { value: 7, label: '近 7 天' },
  { value: 14, label: '近 14 天' },
  { value: 30, label: '近 30 天' },
  { value: 60, label: '近 60 天' },
  { value: 90, label: '近 90 天' },
]

const CHART_HEIGHT = 300

/**
 * UI 趋势看板（三期 D）
 * - 按日聚合：通过率 / 平均耗时 / 平均置信度 / 自愈率四条曲线
 * - flaky 用例识别：近 N 次执行成功/失败翻转 或 自愈频率超阈值
 */
export default function UIDashboard() {
  const { currentProject } = useProjectStore()
  const projectId = currentProject?.id || 0
  const isDark = useThemeStore((s) => s.isDark)

  const [days, setDays] = useState(30)
  const [trend, setTrend] = useState<UITrendData | null>(null)
  const [loading, setLoading] = useState(false)
  // flaky 表格展示用例名（由用例列表映射）
  const [scriptNames, setScriptNames] = useState<Record<number, string>>({})

  const fetchData = useCallback(async () => {
    if (!projectId) return
    setLoading(true)
    try {
      const [data, scriptsRes] = await Promise.all([
        uiApi.getTrends(projectId, days),
        uiApi.listScripts({ project_id: projectId, page: 1, page_size: 100 }),
      ])
      setTrend(data)
      const names: Record<number, string> = {}
      scriptsRes.data.forEach((s) => { names[s.id] = s.name })
      setScriptNames(names)
    } finally {
      setLoading(false)
    }
  }, [projectId, days])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  // 图表主题色（跟随明暗主题）
  const axisColor = isDark ? 'rgba(255,255,255,0.45)' : 'rgba(15,23,42,0.55)'
  const splitColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.06)'
  const tooltipStyle = isDark
    ? { backgroundColor: '#1f2937', borderColor: '#374151', textStyle: { color: '#e5e7eb' } }
    : {}
  const grid = { left: 48, right: 48, top: 40, bottom: 28 }

  /** 图表一：通过率 / 自愈率（%） */
  const rateOption = {
    backgroundColor: 'transparent',
    tooltip: { trigger: 'axis', ...tooltipStyle, valueFormatter: (v: number) => `${v}%` },
    legend: { data: ['通过率', '自愈率'], textStyle: { color: axisColor }, top: 0 },
    grid,
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: trend?.daily.map((d) => d.date.slice(5)) || [],
      axisLine: { lineStyle: { color: splitColor } },
      axisLabel: { color: axisColor },
    },
    yAxis: {
      type: 'value', min: 0, max: 100,
      axisLabel: { color: axisColor, formatter: '{value}%' },
      splitLine: { lineStyle: { color: splitColor } },
    },
    series: [
      {
        name: '通过率', type: 'line', smooth: true, connectNulls: true,
        data: trend?.daily.map((d) => d.pass_rate) || [],
        itemStyle: { color: '#10b981' }, lineStyle: { width: 2.5 },
        areaStyle: { color: 'rgba(16,185,129,0.10)' },
      },
      {
        name: '自愈率', type: 'line', smooth: true, connectNulls: true,
        data: trend?.daily.map((d) => d.heal_rate) || [],
        itemStyle: { color: '#f59e0b' }, lineStyle: { width: 2 },
      },
    ],
  }

  /** 图表二：平均耗时（ms，左轴）/ 平均断言置信度（右轴） */
  const durationOption = {
    backgroundColor: 'transparent',
    tooltip: { trigger: 'axis', ...tooltipStyle },
    legend: { data: ['平均耗时', '平均置信度'], textStyle: { color: axisColor }, top: 0 },
    grid,
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: trend?.daily.map((d) => d.date.slice(5)) || [],
      axisLine: { lineStyle: { color: splitColor } },
      axisLabel: { color: axisColor },
    },
    yAxis: [
      {
        type: 'value', name: '耗时', nameTextStyle: { color: axisColor },
        axisLabel: { color: axisColor }, splitLine: { lineStyle: { color: splitColor } },
      },
      {
        type: 'value', name: '置信度', min: 0, max: 100, nameTextStyle: { color: axisColor },
        axisLabel: { color: axisColor }, splitLine: { show: false },
      },
    ],
    series: [
      {
        name: '平均耗时', type: 'line', smooth: true,
        data: trend?.daily.map((d) => d.avg_duration_ms) || [],
        itemStyle: { color: '#6366f1' }, lineStyle: { width: 2 },
        areaStyle: { color: 'rgba(99,102,241,0.10)' },
      },
      {
        name: '平均置信度', type: 'line', smooth: true, yAxisIndex: 1, connectNulls: true,
        data: trend?.daily.map((d) => d.avg_confidence) || [],
        itemStyle: { color: '#06b6d4' }, lineStyle: { width: 2, type: 'dashed' },
      },
    ],
  }

  const flakyColumns: ColumnsType<UIFlakyItem> = [
    { title: 'ID', dataIndex: 'script_id', width: 70, render: (v: number) => <Tag>#{v}</Tag> },
    {
      title: '用例',
      dataIndex: 'script_id',
      width: 200,
      ellipsis: true,
      render: (v: number) => scriptNames[v] || `脚本 #${v}`,
    },
    { title: '近窗口执行', dataIndex: 'runs', width: 100, render: (v: number) => <span style={{ fontSize: 13 }}>{v} 次</span> },
    {
      title: '通过/失败',
      key: 'pf',
      width: 110,
      render: (_: unknown, r: UIFlakyItem) => (
        <span style={{ fontSize: 13 }}>
          <span style={{ color: '#10b981' }}>{r.passes}</span> / <span style={{ color: '#ef4444' }}>{r.fails}</span>
        </span>
      ),
    },
    {
      title: '结果翻转',
      dataIndex: 'flips',
      width: 100,
      sorter: (a: UIFlakyItem, b: UIFlakyItem) => a.flips - b.flips,
      render: (v: number) => (v > 0 ? <Tag color="orange">{v} 次</Tag> : <span style={{ fontSize: 13 }}>0 次</span>),
    },
    {
      title: '自愈频率',
      dataIndex: 'heal_rate',
      width: 110,
      sorter: (a: UIFlakyItem, b: UIFlakyItem) => a.heal_rate - b.heal_rate,
      render: (v: number) => (
        <Tooltip title="平均每次执行触发的自愈次数">
          <Tag color={v >= 0.5 ? 'red' : 'default'}>{v.toFixed(2)} 次/次</Tag>
        </Tooltip>
      ),
    },
    { title: '识别原因', dataIndex: 'reason', ellipsis: true, render: (v: string) => <span style={{ fontSize: 12, color: 'var(--au-slate-muted)' }}>{v}</span> },
  ]

  const statCards = [
    { title: '执行总数', value: trend?.summary.total_executions ?? 0 },
    { title: '通过', value: trend?.summary.total_passed ?? 0 },
    { title: '失败/错误', value: trend?.summary.total_failed ?? 0 },
    { title: 'flaky 用例', value: trend?.summary.flaky_count ?? 0 },
  ]

  return (
    <div className="aurora-page aurora-page--fit">
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Title level={4} style={{ margin: 0, fontWeight: 600 }}>趋势看板</Title>
          <Tag color="blue">通过率 / 耗时 / 置信度 / 自愈率</Tag>
          <Tag color="red">flaky 识别</Tag>
        </div>
        <Space>
          <Select value={days} onChange={setDays} options={DAYS_OPTIONS} style={{ width: 120 }} />
          <Button onClick={fetchData} loading={loading}>刷新</Button>
        </Space>
      </div>

      {/* 汇总统计卡 */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
        {statCards.map((c) => (
          <div
            key={c.title}
            style={{
              flex: '1 1 150px', minWidth: 150,
              background: 'var(--au-header-bg)',
              border: '1px solid var(--au-border)',
              borderRadius: 10,
              padding: '12px 16px',
            }}
          >
            <Statistic
              title={<span style={{ fontSize: 12, color: 'var(--au-slate-muted)' }}>{c.title}</span>}
              value={c.value}
              valueStyle={{ fontSize: 22, fontWeight: 600, color: 'var(--au-slate)' }}
            />
          </div>
        ))}
      </div>

      {/* 曲线区：通过率/自愈率 + 耗时/置信度 */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <div
          style={{
            flex: '1 1 460px', minWidth: 380,
            background: 'var(--au-header-bg)', border: '1px solid var(--au-border)', borderRadius: 10, padding: '12px 8px 4px',
          }}
        >
          <ReactECharts option={rateOption} style={{ height: CHART_HEIGHT }} notMerge lazyUpdate />
        </div>
        <div
          style={{
            flex: '1 1 460px', minWidth: 380,
            background: 'var(--au-header-bg)', border: '1px solid var(--au-border)', borderRadius: 10, padding: '12px 8px 4px',
          }}
        >
          <ReactECharts option={durationOption} style={{ height: CHART_HEIGHT }} notMerge lazyUpdate />
        </div>
      </div>

      {/* flaky 用例识别 */}
      <div className="content-card" style={{ padding: '16px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, padding: '0 20px' }}>
          <Title level={5} style={{ margin: 0 }}>flaky 用例识别</Title>
          <Tag color="red">{trend?.flaky.length || 0} 个</Tag>
          <span style={{ fontSize: 12, color: 'var(--au-slate-muted)' }}>
            判定：近 20 次有结论执行中成功/失败翻转，或平均每次执行自愈 ≥ 0.5 次
          </span>
        </div>
        <Table
          columns={flakyColumns}
          dataSource={trend?.flaky || []}
          rowKey="script_id"
          loading={loading}
          pagination={false}
          locale={{ emptyText: '窗口期内未识别到 flaky 用例' }}
        />
      </div>
    </div>
  )
}
